<template>
  <div class="viz" :class="{ paused: !active }">
    <!-- 背景光晕层 -->
    <div class="glow" />
    <!-- 主舞台:柱子 + 倒影 -->
    <div class="stage">
      <div class="row">
        <div
            v-for="bar in bars"
            :key="bar.id"
            class="bar"
            :style="{
            '--h': bar.hue,
            '--s': bar.scale,
            '--peak': bar.peak,
            width: barWidth + 'px',
          }"
        >
          <span class="cap" />
        </div>
      </div>
      <!-- 倒影 -->
      <div class="row reflection">
        <div
            v-for="bar in bars"
            :key="'r' + bar.id"
            class="bar"
            :style="{
            '--h': bar.hue,
            '--s': bar.scale,
            '--peak': bar.peak,
            width: barWidth + 'px',
          }"
        >
          <span class="cap" />
        </div>
      </div>
    </div>
    <!-- 地平线 -->
    <div class="horizon" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  maxBars: { type: Number, default: 16 },
  barWidth: { type: Number, default: 16 },
  interval: { type: Number, default: 90 },
  active: { type: Boolean, default: true },
});

const bars = ref([]);
let raf = null;
let timer = null;
let t0 = 0;

function init() {
  t0 = performance.now();
  bars.value = Array.from({ length: props.maxBars }, (_, i) => ({
    id: i,
    scale: 0.06,
    peak: 0.06,
    hue: 190 + (i / Math.max(1, props.maxBars - 1)) * 130, // 青→紫→粉
  }));
}

// 计算一帧的目标振幅(0~1)
function targetAt(i, t) {
  const phase = i * 0.55;
  const w1 = Math.sin(t * 0.004 + phase);
  const w2 = Math.sin(t * 0.011 + phase * 1.8) * 0.35;
  const w3 = Math.sin(t * 0.023 + phase * 3.1) * 0.18;
  const jitter = (Math.random() - 0.5) * 0.15;
  const v = 0.5 + 0.35 * w1 + w2 + w3 + jitter;
  return Math.min(1, Math.max(0.06, v));
}

function step() {
  const t = performance.now() - t0;
  for (const b of bars.value) {
    const target = targetAt(b.id, t);
    // 缓动逼近目标 → 平滑上升下降
    b.scale += (target - b.scale) * 0.35;
    // 峰值拖尾:peak 只慢速回落,形成"余韵"
    if (b.scale > b.peak) b.peak = b.scale;
    else b.peak += (b.scale - b.peak) * 0.04;
  }
}

function loop() {
  step();
  raf = requestAnimationFrame(loop);
}

function start() {
  if (raf) return;
  loop();
  // 用 interval 控制"数据更新节奏",但渲染走 rAF
  if (timer) clearInterval(timer);
  timer = setInterval(() => { /* 占位:接入真实音频时在此取数据 */ }, props.interval);
}
function stop() {
  if (raf) { cancelAnimationFrame(raf); raf = null; }
  if (timer) { clearInterval(timer); timer = null; }
}

onMounted(() => { init(); if (props.active) start(); });
onBeforeUnmount(stop);
watch(() => props.active, (v) => (v ? start() : stop()));
</script>

<style scoped>
.viz {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  border-radius: 18px;
  /* 深空底 */
  background:
      radial-gradient(120% 90% at 50% 110%, rgba(70, 110, 255, 0.18), transparent 60%),
      radial-gradient(90% 70% at 50% 0%, rgba(180, 80, 255, 0.10), transparent 60%),
      linear-gradient(180deg, #070912 0%, #0d1022 60%, #05060d 100%);
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -40px 80px rgba(0, 0, 0, 0.6);
}

/* 顶部环境光晕,整体呼吸感 */
.glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(60% 40% at 50% 100%, rgba(120, 170, 255, 0.22), transparent 70%);
  filter: blur(20px);
  pointer-events: none;
  animation: breathe 6s ease-in-out infinite;
}
@keyframes breathe {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}

/* 舞台:上排正常 + 下排倒影 */
.stage {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 20px;
}

.row {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 6px;
  height: 50%;
}

/* 倒影:垂直翻转 + 渐隐 + 模糊 */
.reflection {
  transform: scaleY(-1);
  opacity: 0.35;
  filter: blur(2px);
  -webkit-mask-image: linear-gradient(to top, transparent 20%, #000 100%);
  mask-image: linear-gradient(to top, transparent 20%, #000 100%);
}

/* 单根光柱 */
.bar {
  position: relative;
  height: 100%;
  border-radius: 8px 8px 3px 3px;
  transform: scaleY(var(--s, 0.06));
  transform-origin: bottom center;
  will-change: transform;
  /* 主体:上亮下深的发光渐变 */
  background: linear-gradient(
      to top,
      hsl(var(--h) 80% 35% / 0.9),
      hsl(var(--h) 90% 55%) 45%,
      hsl(var(--h) 100% 72%) 85%,
      hsl(var(--h) 100% 90%)
  );
  /* 外发光 + 边缘内高光,像霓虹灯管 */
  box-shadow:
      0 0 10px hsl(var(--h) 100% 65% / 0.55),
      0 0 24px hsl(var(--h) 100% 60% / 0.35),
      inset 0 1px 1px rgba(255, 255, 255, 0.6),
      inset 0 -6px 12px hsl(var(--h) 90% 40% / 0.5);
}

/* 顶部圆头高光,像灯管顶端亮点 */
.cap {
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  height: 4px;
  border-radius: 50%;
  background: hsl(var(--h) 100% 92%);
  box-shadow:
      0 0 8px hsl(var(--h) 100% 80%),
      0 0 16px hsl(var(--h) 100% 70%);
}

/* 峰值拖尾:一条亮线在 peak 位置 */
.bar::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(var(--peak, 0.06) * 100%);
  border-radius: inherit;
  background: linear-gradient(to top, transparent 85%, rgba(255, 255, 255, 0.5));
  pointer-events: none;
  mix-blend-mode: screen;
}

/* 地平线:地平线处的强光带 */
.horizon {
  position: absolute;
  left: 8%;
  right: 8%;
  top: 50%;
  height: 1px;
  transform: translateY(-50%);
  background: linear-gradient(
      to right,
      transparent,
      rgba(140, 180, 255, 0.85),
      #cfe0ff,
      rgba(140, 180, 255, 0.85),
      transparent
  );
  box-shadow:
      0 0 12px rgba(120, 170, 255, 0.9),
      0 0 30px rgba(120, 170, 255, 0.5);
}

.paused .glow { animation-play-state: paused; }

@media (prefers-reduced-motion: reduce) {
  .glow { animation: none; }
}
</style>